/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * MobileBottomNav — Role-aware fixed bottom navigation for mobile.
 * Shows top 4 priority items + "More" drawer with complete nav grid.
 * Features: notification badge, active pill, haptic feedback, safe area.
 *
 * Props:
 *   navItems — full sidebar nav array from DashboardLayout
 *   type     — 'couple' | 'vendor' | 'admin' | 'venue_owner' | 'coordinator'
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import notificationService from '@services/notificationService';

// ─── Priority items for each role (shown in bottom bar) ───
const ROLE_PRIORITIES = {
  couple: [
    { to: '/dashboard/home', icon: 'bi-house-heart', label: 'Home' },
    { to: '/dashboard/guests', icon: 'bi-people', label: 'Guests' },
    { to: '/dashboard/budget', icon: 'bi-wallet2', label: 'Budget' },
    { to: '/dashboard/notifications', icon: 'bi-bell', label: 'Alerts', badge: true },
  ],
  vendor: [
    { to: '/vendor', icon: 'bi-house', label: 'Home', end: true },
    { to: '/vendor/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
    { to: '/vendor/messages', icon: 'bi-chat', label: 'Messages' },
    { to: '/vendor/portfolio', icon: 'bi-collection', label: 'Portfolio' },
  ],
  admin: [
    { to: '/admin', icon: 'bi-shield-lock', label: 'Dashboard', end: true },
    { to: '/admin/users', icon: 'bi-people', label: 'Users' },
    { to: '/admin/vendors', icon: 'bi-shop', label: 'Vendors' },
    { to: '/admin/revenue', icon: 'bi-graph-up', label: 'Revenue' },
  ],
  venue_owner: [
    { to: '/venue', icon: 'bi-house', label: 'Home', end: true },
    { to: '/venue/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
    { to: '/venue/availability', icon: 'bi-calendar-x', label: 'Availability' },
    { to: '/venue/photos', icon: 'bi-images', label: 'Photos' },
  ],
  coordinator: [
    { to: '/coordinator', icon: 'bi-clipboard2-check', label: 'Dashboard', end: true },
    { to: '/coordinator/weddings', icon: 'bi-heart', label: 'Weddings' },
    { to: '/coordinator/calendar', icon: 'bi-calendar3', label: 'Calendar' },
    { to: '/coordinator/vendors', icon: 'bi-shop', label: 'Vendors' },
  ],
};

// ─── Styles injected once ───
const STYLE_ID = '__mobile_bnav_v2__';
const ensureStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .mbn { display: none; }
    .mbn-drawer-overlay { display: none; }
    .mbn-drawer { display: none; }
    @media (max-width: 767px) {
      .mbn {
        display: flex;
        position: fixed;
        bottom: 0; left: 0; right: 0;
        background: rgba(255,255,255,0.97);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-top: 1px solid #E5E7EB;
        z-index: 1050;
        padding: 2px 0 calc(2px + env(safe-area-inset-bottom, 0px));
        box-shadow: 0 -2px 16px rgba(0,0,0,0.06);
        justify-content: space-around;
        align-items: stretch;
      }
      .mbn__item {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center;
        text-decoration: none;
        color: #9CA3AF;
        font-size: 0.58rem; font-weight: 500;
        padding: 6px 4px 4px;
        min-width: 56px; flex: 1;
        position: relative;
        transition: color 0.15s;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
      }
      .mbn__item i { font-size: 1.15rem; margin-bottom: 1px; transition: transform 0.15s; }
      .mbn__item.active { color: #9B30FF; }
      .mbn__item.active i { transform: scale(1.12); }
      .mbn__item.active::after {
        content: '';
        position: absolute; top: 0; left: 50%; transform: translateX(-50%);
        width: 28px; height: 3px;
        background: #9B30FF; border-radius: 0 0 3px 3px;
      }
      .mbn__item:active i { transform: scale(0.9); }
      .mbn__badge {
        position: absolute; top: 2px; right: calc(50% - 18px);
        min-width: 16px; height: 16px; line-height: 16px;
        font-size: 0.5rem; font-weight: 700; text-align: center;
        background: #EF4444; color: #fff; border-radius: 8px;
        padding: 0 4px; animation: mbn_pop 0.3s ease;
      }
      @keyframes mbn_pop {
        0% { transform: scale(0); }
        70% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      .mbn__more-btn {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center;
        background: none; border: none; cursor: pointer;
        color: #9CA3AF; font-size: 0.58rem; font-weight: 500;
        padding: 6px 4px 4px; min-width: 56px; flex: 1;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
        position: relative;
        transition: color 0.15s;
      }
      .mbn__more-btn i { font-size: 1.15rem; margin-bottom: 1px; transition: transform 0.2s; }
      .mbn__more-btn.open { color: #9B30FF; }
      .mbn__more-btn.open i { transform: rotate(45deg); }
      .mbn__more-btn.has-active { color: #9B30FF; }
      .mbn__more-btn.has-active::after {
        content: '';
        position: absolute; top: 0; left: 50%; transform: translateX(-50%);
        width: 28px; height: 3px;
        background: #9B30FF; border-radius: 0 0 3px 3px;
      }
      .mbn__more-btn:active i { transform: scale(0.9); }

      /* Drawer */
      .mbn-drawer-overlay {
        display: block;
        position: fixed; inset: 0; background: rgba(0,0,0,0.3);
        z-index: 1049; opacity: 0; pointer-events: none;
        transition: opacity 0.25s;
      }
      .mbn-drawer-overlay.open { opacity: 1; pointer-events: auto; }
      .mbn-drawer {
        display: block;
        position: fixed; bottom: 0; left: 0; right: 0;
        background: #fff; border-radius: 16px 16px 0 0;
        z-index: 1051;
        transform: translateY(100%);
        transition: transform 0.3s cubic-bezier(0.32,0.72,0,1);
        max-height: 70vh;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
        box-shadow: 0 -8px 32px rgba(0,0,0,0.12);
      }
      .mbn-drawer.open { transform: translateY(0); }
      .mbn-drawer__handle {
        width: 36px; height: 4px; background: #D1D5DB;
        border-radius: 2px; margin: 10px auto 6px;
      }
      .mbn-drawer__title {
        font-size: 0.8rem; font-weight: 700; color: #1F2937;
        padding: 4px 20px 10px; border-bottom: 1px solid #F3F4F6;
      }
      .mbn-drawer__grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 2px; padding: 12px 8px;
      }
      .mbn-drawer__item {
        display: flex; flex-direction: column; align-items: center;
        text-decoration: none; color: #6B7280;
        padding: 12px 4px; border-radius: 10px;
        font-size: 0.65rem; font-weight: 500;
        text-align: center;
        transition: background 0.15s, color 0.15s;
        -webkit-tap-highlight-color: transparent;
      }
      .mbn-drawer__item i { font-size: 1.3rem; margin-bottom: 4px; }
      .mbn-drawer__item.active { color: #9B30FF; background: #F5F0FF; }
      .mbn-drawer__item:active { background: #F3F4F6; }
    }
  `;
  document.head.appendChild(style);
};

const MobileBottomNav = ({ navItems = [], type = 'couple' }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 767);
  const location = useLocation();
  const touchStartY = useRef(0);

  // Only render on mobile — prevents unstyled flash on desktop
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 767);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const priorities = ROLE_PRIORITIES[type] || ROLE_PRIORITIES.couple;

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Unread count for notification badge
  useEffect(() => {
    const load = async () => {
      try {
        const r = await notificationService.getUnreadCount();
        setUnreadCount(r.data?.count || 0);
      } catch {}
    };
    load();
    const interval = setInterval(load, 120000); // 2 min — NotificationBell also polls at 60s
    return () => clearInterval(interval);
  }, []);

  // Haptic feedback
  const haptic = useCallback(() => {
    if ('vibrate' in navigator) navigator.vibrate(8);
  }, []);

  // Swipe-down to close drawer
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (dy > 60) setDrawerOpen(false);
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  ensureStyles();

  // All hooks declared above — safe to return early now
  if (!isMobile) return null;

  // Drawer items = full nav minus the bottom bar priorities
  const priorityPaths = new Set(priorities.map(p => p.to));
  const drawerItems = navItems.filter(item => !priorityPaths.has(item.to));

  // Check if active route is inside the drawer
  const isDrawerItemActive = drawerItems.some(
    item => location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to + '/'))
  );

  return (
    <>
      {/* Bottom Bar */}
      <nav className="mbn" role="navigation" aria-label="Mobile navigation">
        {priorities.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `mbn__item ${isActive ? 'active' : ''}`}
            onClick={haptic}
          >
            <i className={`bi ${item.icon}`} />
            {item.badge && unreadCount > 0 && (
              <span className="mbn__badge" key={unreadCount}>{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
            <span>{item.label}</span>
          </NavLink>
        ))}
        <button
          className={`mbn__more-btn ${drawerOpen ? 'open' : ''} ${isDrawerItemActive && !drawerOpen ? 'has-active' : ''}`}
          onClick={() => { haptic(); setDrawerOpen(prev => !prev); }}
          aria-expanded={drawerOpen}
          aria-label="More navigation items"
        >
          <i className={`bi ${drawerOpen ? 'bi-x-lg' : 'bi-grid-3x3-gap'}`} />
          <span>More</span>
        </button>
      </nav>

      {/* Overlay */}
      <div
        className={`mbn-drawer-overlay ${drawerOpen ? 'open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* Drawer */}
      <div
        className={`mbn-drawer ${drawerOpen ? 'open' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="mbn-drawer__handle" />
        <div className="mbn-drawer__title">
          <i className="bi bi-grid me-2" style={{ color: '#9B30FF' }}></i>
          All Pages
        </div>
        <div className="mbn-drawer__grid">
          {drawerItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `mbn-drawer__item ${isActive ? 'active' : ''}`}
              onClick={() => { haptic(); setDrawerOpen(false); }}
            >
              <i className={`bi ${item.icon}`} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </>
  );
};

export default MobileBottomNav;
